import { Link } from "react-router-dom";
import { Anchor, Fuel, Store, Sailboat, ArrowRight, Phone } from "lucide-react";
import { SITE, PHOTOS, MARINA_SERVICES } from "../data/content";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";

export default function Marina() {
  return (
    <main data-testid="marina-page">
      <PageHero
        image={PHOTOS.heroDrone}
        eyebrow="Marina"
        title="Slips, gas dock & lake access — straight from the point."
        subtitle="Day slips and long-term rentals, plus the everyday essentials to keep your day on the water moving."
      />

      {/* INTRO */}
      <section className="py-24 bg-[var(--ec-cream)]" data-testid="marina-intro">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ec-lake)] mb-3">
              Elk Creek Marina
            </div>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--ec-navy)] leading-[1.05]">
              The friendly, no-fuss marina at the north end of Tenkiller.
            </h2>
            <p className="mt-6 text-[var(--ec-charcoal)]/80 leading-relaxed">
              Enjoy the tranquility of Elk Creek's Tenkiller surrounds. We are a family-friendly facility located near the north end of Lake Tenkiller — 12,900 acres of water and 130 miles of shoreline that has been operational since 1953. We offer cabins, docks, boat rentals, paddleboards and more, plus Elk Creek Resort clothing, a gas dock and a convenience store for basic boating needs.
            </p>
            <p className="mt-4 text-[var(--ec-charcoal)]/80 leading-relaxed">
              <span className="font-semibold text-[var(--ec-navy)]">Slips available!</span> The majority of our newly constructed dock spaces were occupied in a matter of weeks — we are continually adding slips. If you're interested in a dock, come over and say hello or give us a call at {SITE.phoneDisplay}.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={`tel:${SITE.phoneRaw}`}
                data-testid="marina-call-button"
                className="inline-flex items-center gap-2 bg-[var(--ec-navy)] text-white hover:bg-[var(--ec-lake)] font-semibold px-7 py-4 rounded-full btn-lift"
              >
                <Phone className="w-4 h-4" /> Check Slip Availability
              </a>
              <a
                href={SITE.directions}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="marina-directions-button"
                className="inline-flex items-center gap-2 border border-[var(--ec-navy)] text-[var(--ec-navy)] hover:bg-[var(--ec-navy)] hover:text-white font-semibold px-7 py-4 rounded-full btn-lift"
              >
                Get Directions<span className="sr-only"> (opens in new tab)</span>
              </a>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-6" delay={150}>
            <div className="img-zoom rounded-3xl overflow-hidden shadow-md card-lift">
              <img
                src={PHOTOS.marinaBoatDeck}
                alt="Elk Creek Marina view from the water"
                className="w-full h-full object-cover aspect-[4/3]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 bg-white" data-testid="marina-services">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeading
            eyebrow="Services"
            title="What you'll find at the dock."
            subtitle="The everyday essentials to keep your day on the water simple and stress-free."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MARINA_SERVICES.map((s, i) => {
              const Icons = [Anchor, Anchor, Fuel, Store, Sailboat, Anchor];
              const Icon = Icons[i] || Anchor;
              return (
                <Reveal key={s.title} delay={i * 70}>
                  <div className="h-full bg-[var(--ec-cream)] rounded-2xl p-7 border border-[var(--ec-sand)] card-lift" data-testid={`marina-service-${i}`}>
                    <div className="w-11 h-11 rounded-full bg-[var(--ec-lake)]/10 flex items-center justify-center mb-5">
                      <Icon className="w-5 h-5 text-[var(--ec-lake)]" />
                    </div>
                    <h3 className="font-display text-2xl text-[var(--ec-navy)]">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[var(--ec-charcoal)]/75">{s.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SLIPS PANEL */}
      <section className="py-24 bg-[var(--ec-navy)] text-white" data-testid="marina-slips-panel">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="img-zoom rounded-3xl overflow-hidden shadow-xl">
              <img src={PHOTOS.marinaPontoonDocked} alt="Pontoon boat in a slip at Elk Creek Marina" className="w-full h-full object-cover aspect-[4/3]" />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={120}>
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ec-lake-light)] mb-3">
              Slip Rentals
            </div>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-[1.05]">
              Day slips & yearly rentals available.
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed">
              We have a limited number of day slips for visitors and yearly rentals fill up quickly each season. Call ahead — we're happy to walk you through current options.
            </p>
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="marina-slips-call"
              className="mt-8 inline-flex items-center gap-2 bg-[var(--ec-lake)] hover:bg-[var(--ec-lake-dark)] text-white font-semibold px-7 py-4 rounded-full btn-lift"
            >
              <Phone className="w-4 h-4" /> Call {SITE.phoneDisplay}
            </a>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[var(--ec-cream)]" data-testid="marina-cta">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 text-center">
          <h2 className="font-display text-4xl sm:text-5xl text-[var(--ec-navy)]">Heading our way?</h2>
          <p className="mt-4 text-[var(--ec-charcoal)]/75 max-w-xl mx-auto">
            Roll right up to the gas dock, grab a snack at the store, or settle in for the weekend. We'll meet you at the water.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/contact"
              data-testid="marina-book-cta"
              className="inline-flex items-center gap-2 bg-[var(--ec-navy)] text-white hover:bg-[var(--ec-lake)] font-semibold px-7 py-4 rounded-full btn-lift"
            >
              Book Your Stay <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="marina-contact-cta"
              className="inline-flex items-center gap-2 border border-[var(--ec-navy)] text-[var(--ec-navy)] hover:bg-[var(--ec-navy)] hover:text-white font-semibold px-7 py-4 rounded-full btn-lift"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
